import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Users } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { usePipelineMeta, StageBadge, ChecklistPill, DocStatusBadge } from "@/lib/pipeline";
import { CandidateForm } from "@/components/CandidateForm";
import { TagList } from "@/components/MultiTagSelect";

function Filter({ value, onChange, options, all, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-56 bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{all}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-${v}`}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function CandidatesPage({ queue = false }) {
  const meta = usePipelineMeta();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ rank: "all", vessel_type: "all", availability: "all", doc_status: "all" });
  const [addOpen, setAddOpen] = useState(false);

  const load = () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all"));
    if (queue) params.queue = true;
    api.get("/candidates", { params }).then(({ data }) => setItems(data)).catch((err) => { toast.error(formatApiError(err)); setItems([]); });
  };
  useEffect(load, [f, queue]); // eslint-disable-line

  const caps = meta?.caps || {};
  const title = queue ? "Docs Verification Queue" : "Candidates";
  return (
    <div className="space-y-6" data-testid={queue ? "docs-queue-page" : "candidates-page"}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Pipeline</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>{title}</h1>
          <div className="text-sm text-slate-500 mt-1">
            {queue ? "Candidates in Sourcing or Docs Verification, waiting for the 38-point check." : caps.view_all ? "All candidates in the system." : "Candidates you have added."}
          </div>
        </div>
        {!queue && caps.can_add && (
          <Button onClick={() => setAddOpen(true)} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="add-candidate-btn"><Plus className="w-4 h-4 mr-1" /> Add candidate</Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="candidate-filters">
        <Filter value={f.rank} onChange={(v) => setF({ ...f, rank: v })} all="All ranks" options={(meta?.ranks || []).map((r) => [r, r])} testid="filter-rank" />
        <Filter value={f.vessel_type} onChange={(v) => setF({ ...f, vessel_type: v })} all="All vessel types" options={(meta?.vessel_types || []).map((t) => [t, t])} testid="filter-vessel-type" />
        <Filter value={f.availability} onChange={(v) => setF({ ...f, availability: v })} all="Any availability" options={(meta?.availability || []).map((a) => [a, a])} testid="filter-availability" />
        <Filter value={f.doc_status} onChange={(v) => setF({ ...f, doc_status: v })} all="Any document status" options={[["expired", "Expired"], ["expiring", "Expiring Soon"], ["valid", "Valid"], ["none", "No docs"]]} testid="filter-doc-status" />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="candidates-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>{[["Name", 1], ["Rank", 1], ["CDC"], ["Availability"], ["Docs"], ["Checklist"], ["Stage"]].map(([h, a]) => <th key={h} className={`px-4 py-3 text-left font-medium ${a ? "" : "hidden md:table-cell"}`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {items?.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/candidates/${c.id}`)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`candidate-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{c.full_name}<div className="md:hidden mt-1"><StageBadge stage={c.current_stage} /></div></td>
                  <td className="px-4 py-3 text-slate-700"><TagList items={c.ranks} testid={`candidate-ranks-${c.id}`} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 hidden md:table-cell">{c.cdc_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{c.availability_status}</td>
                  <td className="px-4 py-3 hidden md:table-cell"><DocStatusBadge status={c.doc_status} testid={`candidate-doc-status-${c.id}`} /></td>
                  <td className="px-4 py-3 hidden md:table-cell"><ChecklistPill done={c.checklist_done} total={c.checklist_total} testid={`candidate-checklist-${c.id}`} /></td>
                  <td className="px-4 py-3 hidden md:table-cell"><StageBadge stage={c.current_stage} testid={`candidate-stage-${c.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!items && <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>}
        {items?.length === 0 && <div className="py-14 text-center text-sm text-slate-500" data-testid="candidates-empty-state"><Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />No candidates found.</div>}
      </div>
      <CandidateForm open={addOpen} onOpenChange={setAddOpen} meta={meta} onSaved={(id) => navigate(`/candidates/${id}`)} />
    </div>
  );
}
